// The chat docked in a Meeting Prep deck (Lane 1355, deck workspace chat slices 1-2).
// The same chat as the sourcing event workspace (Ryan's ruling R1): the shared
// workspace chat frame, dock and state, with a deck transport. A question never
// reloads or touches the deck editor. A slide the chat prepares is shown on a card
// (DeckChatProposalCard); adding it, a typed yes to it and Undo run through the deck
// page's save queue (`applyChange`), which pauses editing and reloads the deck.
//
// The server decides whether the chat exists here (flag, Mirror chat, a native deck
// the buyer owns): anything but an available chat renders nothing at all.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { requestChatTurn } from '../../utils/chatProgressStream';
import useWorkspaceChat from '../workspace-chat/useWorkspaceChat';
import useDockedChat, { CHAT_WIDTH } from '../workspace-chat/useDockedChat';
import { ChatDock } from '../workspace-chat/ChatDock';
import WorkspaceChatPanel from '../workspace-chat/WorkspaceChatPanel';
import DeckChatProposalCard from './DeckChatProposalCard';
import ChatBubble from '../workspace-chat/ChatBubble';
import focusChat from '../workspace-chat/focusChat';
import ChatRail from '../workspace-chat/ChatRail';
import '../ui/DataPrimitives.css';   // the .ui-page scope the sourcing chat sits in
import './deckChat.css';

const RAIL_WIDTH = 220;           // the deck's page rail (DeckAssembler.linear.css .dk-normal)
const SLIDE_MIN = 520;            // the slide area beside the chat never gets narrower

async function readJson(response) {
  let body = null;
  try { body = await response.json(); } catch (e) { body = null; }
  if (!response.ok) {
    const detail = body && body.detail;
    throw new Error((detail && detail.message) || 'The chat could not load. Try again.');
  }
  return body;
}

async function call(url, options) {
  const response = await fetch(url, options);
  return readJson(response);
}

function makeTransport({ apiBase, compositionId, headersRef, contextRef, readyRef, applyRef,
  settledRef, onApplied }) {
  const base = `${apiBase}/api/decks/compositions/${compositionId}/chat`;
  const json = () => ({ ...headersRef.current(), 'Content-Type': 'application/json' });
  // The deck the buyer was looking at when they pressed Send, per message, and the
  // prepared slide on screen then (the typed yes means that one); a retry keeps its
  // first context (the server judges it by that too).
  const sentFrom = new Map();
  const turn = (body, onEvent, extra) => requestChatTurn({
    streamUrl: `${base}/turns/stream`,
    fallbackUrl: `${base}/turns`,
    headers: json(),
    body: { ...body, ...extra },
    onEvent,
  });
  return {
    capture: (clientTurnId) => {
      if (sentFrom.has(clientTurnId)) return;
      const ready = readyRef.current;    // the prepared slide on screen at Send
      const pin = ({ settling, ...ctx }) => (ready && ctx.may_request
        ? { ...ctx, may_apply: true, previewed_attempt_id: ready.attempt_id,
          previewed_page_digest: ready.page_digest }
        : { ...ctx, may_apply: false });
      const now = contextRef.current();
      // A save or build still running with nothing unsaved (the build right after
      // the deck opens): wait for it, then take the deck as it is (staging
      // 2026-10-01). Unsaved edits are still refused by the server.
      sentFrom.set(clientTurnId, now.settling && settledRef.current
        ? settledRef.current().then(() => pin(contextRef.current()))
        : pin(now));
    },
    // 404: no chat for this deck (switch off, not a native deck, not the owner).
    load: async () => {
      const response = await fetch(base, { headers: headersRef.current() });
      if (response.status === 404) return { available: false };
      return readJson(response);
    },
    create: () => fetch(`${base}/conversations`, { method: 'POST', headers: headersRef.current() })
      .then(readJson),
    // Each message carries the deck the buyer was looking at when they pressed Send.
    // With a prepared slide on screen the message may say yes to it, so it runs in
    // the deck's save queue: editing waits, and the deck reloads if a slide was added.
    send: async (body, onEvent) => {
      const captured = sentFrom.get(body.client_turn_id);
      if (captured && typeof captured.then === 'function') {
        onEvent({ type: 'stage', line: 'Waiting for the deck to finish saving…' });
      }
      const { settling, ...now } = contextRef.current();
      const ctx = (await captured) || { ...now, may_apply: false };
      if (!ctx.may_apply || !applyRef.current) return turn(body, onEvent, ctx);
      const out = await applyRef.current(async (clean) => {
        const result = await turn(body, onEvent, clean ? ctx : { ...ctx, may_apply: false });
        const applied = result.ok && result.data && (result.data.event_cards || [])
          .find((card) => card.state === 'applied');
        return { result, reload: Boolean(applied),
          position: applied ? applied.position : null,
          editRevision: applied ? applied.edit_revision : null };
      });
      if (out && out.reload && onApplied) onApplied();
      return out ? out.result : { ok: false, status: 0 };
    },
    proposal: (id) => call(`${base}/proposals/${id}`, { headers: headersRef.current() }),
    preview: (id, attemptId) => call(`${base}/proposals/${id}/preview?attempt=${attemptId}`,
      { headers: headersRef.current() }),
    confirm: (id, attemptId, pageDigest) => call(`${base}/proposals/${id}/confirm`, {
      method: 'POST', headers: json(),
      body: JSON.stringify({ attempt_id: attemptId, page_digest: pageDigest }) }),
    prepareAgain: (id, body) => call(`${base}/proposals/${id}/prepare-again`, {
      method: 'POST', headers: json(), body: JSON.stringify(body) }),
    latestChange: () => call(`${base}/operations/latest`, { headers: headersRef.current() }),
    undo: (operationId) => call(`${base}/operations/${operationId}/undo`, {
      method: 'POST', headers: json(), body: '{}' }),
  };
}

// The newest prepared slide the buyer can say yes to: its proposal is waiting and its
// newest attempt is ready for the deck as it is now.
function readySlide(chat, shown) {
  const cards = [];
  chat.turns.forEach((t) => (t.event_cards || []).forEach((c) => {
    if (c.type === 'slide_change' && c.proposal_id) cards.push(c);
  }));
  for (let i = cards.length - 1; i >= 0; i -= 1) {
    const view = chat.proposals[cards[i].proposal_id];
    const attempt = view && view.attempt;
    // Only the preview actually on screen (Codex s2 r5 #4).
    if (view && view.status === 'waiting' && !view.operation && attempt
      && attempt.status === 'ready' && !attempt.stale
      && shown[attempt.attempt_id]) return attempt;
  }
  return null;
}

// "Undo last chat change": the deck's newest chat change while it can be undone,
// independent of the conversation shown. The line names the change (Lane 1370).
const CHANGE_LINES = {
  add: 'Slide added from the chat.',
  reword: 'Slide reworded from the chat.',
  remove: 'Slide removed from the chat.',
};
function UndoLastChange({ api, applyChange, refreshKey, revision }) {
  const [change, setChange] = useState(null);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);
  const [focusTick, setFocusTick] = useState(0);
  // Read again when the deck's revision moves (a save here or a reload) and when
  // the tab comes back into view (another tab may have changed the deck).
  useEffect(() => {
    const onFocus = () => setFocusTick((n) => n + 1);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  useEffect(() => {
    let live = true;
    api.latestChange().then((r) => { if (live) setChange(r.operation); }).catch(() => {});
    return () => { live = false; };
  }, [api, refreshKey, revision, focusTick]);
  if (!change || !change.undoable) return null;
  const undo = async () => {
    setError(''); setWorking(true);
    try {
      const out = await applyChange(async (clean) => {
        if (!clean) return { refused: 'Save your edits first, then undo.' };
        const r = await api.undo(change.operation_id);
        return { reload: true, position: r.position, editRevision: r.edit_revision };
      });
      if (out && out.refused) setError(out.refused);
      const r = await api.latestChange();
      setChange(r.operation);
    } catch (e) {
      setError(e.message);
    } finally {
      setWorking(false);
    }
  };
  return (
    <div className="wchat-context dk-chat-undo">
      <span>{CHANGE_LINES[change.mode] || CHANGE_LINES.add}</span>
      <button type="button" className="wchat-btn" disabled={working} onClick={undo}>Undo last chat change</button>
      {error && <span className="wchat-error" role="alert">{error}</span>}
    </div>
  );
}

// The width the dock may take: the deck workspace's own width (it starts right of
// the app menu) minus the page rail and the slide area's minimum. Watched with a
// ResizeObserver, so any change of the workspace's width counts. null: no deck
// workspace on screen (the narrow layout).
function useDockRoom(layout) {
  const [room, setRoom] = useState(null);
  useEffect(() => {
    if (layout !== 'dock') { setRoom(null); return undefined; }
    const shell = document.querySelector('[data-testid="deck-normal-workspace"]');
    if (!shell) return undefined;
    const measure = () => setRoom(Math.floor(shell.getBoundingClientRect().width)
      - RAIL_WIDTH - SLIDE_MIN);
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(shell);
    window.addEventListener('resize', measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [layout]);
  return room;
}

const DOCK_ID = 'dk-chat-dock';

// Lane 1387 (Ryan 2026-10-05): the round Wingman bubble opens the chat again. Beside the
// deck (the dock layout) it sits in its own strip right of the slide area while the chat
// is closed (ChatRail), so it never covers the slide, the toolbar or the notes; the
// narrow drawer keeps its floating bubble. No "Chat with Wingman" or Feedback button.
function DockedDeckChat({ chat, drawer, room, draft, onDraft, api, applyChange,
  getDeckContext, changeTick, shown, onShown }) {
  // Two saved choices: the docked chat starts open like the sourcing chat; the
  // narrow drawer starts closed so it never covers the slide on first sight.
  // useDockedChat keeps the dock within (window width - mainMin), so mainMin is
  // everything that is not room for the dock.
  const dock = useDockedChat(drawer
    ? { storagePrefix: 'dk-chat-drawer', mainMin: 0, defaultOpen: false }
    : { storagePrefix: 'dk-chat', reclampOnResize: true,
      mainMin: room === null ? RAIL_WIDTH + SLIDE_MIN : window.innerWidth - room });
  // Open and close from the bubble, the panel's close button or Escape: focus goes
  // to the message box on open and back to the bubble on close (Lane 1355, the
  // bubble). The selected slide still reaches the server through getDeckContext;
  // only the "Looking at" line is gone (Ryan, 2026-10-01).
  const scopeRef = useRef(null);
  const bubbleRef = useRef(null);
  const moveFocus = useRef(null);
  const setOpen = (next) => {
    moveFocus.current = next ? 'input' : 'bubble';
    dock.setOpen(next);
  };
  useEffect(() => {
    const target = moveFocus.current;
    moveFocus.current = null;
    if (target === 'input' && dock.open && scopeRef.current) {
      focusChat(scopeRef.current);
    } else if (target === 'bubble' && !dock.open && bubbleRef.current) {
      bubbleRef.current.focus();
    }
  }, [dock.open]);
  // Only a key pressed in the chat itself: React passes events from a portalled child
  // (a form opened from the chat, such as the Feedback form of Lane 1382) up through this tree,
  // and Escape there must close the form, not the chat (Codex r1 #1).
  const onKeyDown = (e) => {
    if (!scopeRef.current || !scopeRef.current.contains(e.target)) return;
    if (e.key === 'Escape' && dock.open) { e.stopPropagation(); setOpen(false); }
  };
  const chip = (
    <UndoLastChange api={api} applyChange={applyChange} refreshKey={changeTick}
      revision={getDeckContext().edit_revision} />
  );
  const renderCards = (t) => (t.event_cards || [])
    .filter((c) => c.type === 'slide_change')
    .map((c, i) => (
      <DeckChatProposalCard key={`${c.proposal_id || 'refused'}-${i}`} card={c}
        view={c.proposal_id ? chat.proposals[c.proposal_id] : null} api={api}
        applyChange={applyChange} getDeckContext={getDeckContext} busy={chat.busy}
        shown={shown} onShown={onShown}
        onView={(id, view) => { if (view) chat.setProposalState(id, view); }} />
    ));
  return (
    <div ref={scopeRef} id={DOCK_ID} onKeyDown={onKeyDown}
      className={`ui-page dk-chat-scope${drawer ? ' dk-chat-drawer' : ''}`}
      data-testid="deck-chat">
      {drawer ? (
        <ChatBubble ref={bubbleRef} open={dock.open} controls={DOCK_ID}
          onToggle={() => setOpen(!dock.open)} />
      ) : (
        <ChatRail ref={bubbleRef} open={dock.open} controls={DOCK_ID} manageFocus={false}
          onOpen={() => setOpen(true)} />
      )}
      <ChatDock dock={dock}>
        <WorkspaceChatPanel
          chat={chat}
          title="Chat with Wingman"
          ariaLabel="Chat about this deck"
          unavailableText="Chat is not available for this deck."
          emptyState={(
            <>
              Ask about this deck or the data behind it, for example
              {' '}&ldquo;how has their on-time delivery moved over the last three months?&rdquo;
              {' '}You can also ask me to add a page, reword a slide or remove one, for
              {' '}example &ldquo;add a price history page for our two biggest parts&rdquo; or
              {' '}&ldquo;make the headline on slide 3 shorter&rdquo;.
            </>
          )}
          placeholder="Ask about this deck…"
          onClose={() => setOpen(false)}
          contextChip={chip}
          renderCards={renderCards}
          draft={draft}
          onDraft={onDraft}
        />
      </ChatDock>
    </div>
  );
}

// `getDeckContext()` -> { edit_revision, source_revision, selected_position,
// may_request }: the saved deck the buyer is looking at, read when a message is sent
// (may_request: no unsaved edit and no save queued, running or uncertain). Mount with
// key={compositionId} so another deck starts a fresh chat. `draft` / `onDraft`: the
// unsent message, held by the deck page above its responsive layouts, so Present, a
// dock/drawer switch and the page's own layout switch all keep it.
// `applyChange(fn)`: runs fn(clean) in the deck page's save queue with editing
// paused; fn returns { reload } to reload the deck afterwards (plan 5.1 item 4).
export default function DeckChatDock({ apiBase, compositionId, authHeaders, getDeckContext,
  hidden, layout, draft, onDraft, applyChange, whenSettled }) {
  const headersRef = useRef(authHeaders);
  headersRef.current = authHeaders;
  const contextRef = useRef(getDeckContext);
  contextRef.current = getDeckContext;
  const applyRef = useRef(applyChange);
  applyRef.current = applyChange;
  const settledRef = useRef(whenSettled);
  settledRef.current = whenSettled;
  const readyRef = useRef(null);
  const [changeTick, setChangeTick] = useState(0);
  // Which prepared previews are on screen now (a preview reports itself shown and,
  // when it unmounts or fails, hidden). Kept apart from the server's proposal
  // states, which every turn replaces (Codex s2 r6 #5).
  const [shown, setShown] = useState({});
  const onShown = useCallback((attemptId, visible) => {
    setShown((prev) => (Boolean(prev[attemptId]) === visible ? prev
      : { ...prev, [attemptId]: visible }));
  }, []);
  const transport = useMemo(
    () => makeTransport({ apiBase, compositionId, headersRef, contextRef, readyRef, applyRef,
      settledRef, onApplied: () => setChangeTick((n) => n + 1) }),
    [apiBase, compositionId]);
  // No onChanged: a question never reloads the deck (plan 5.1, Codex r1 #5).
  const chat = useWorkspaceChat({ transport, chatSurface: 'deck' });
  readyRef.current = chat.available ? readySlide(chat, shown) : null;
  const chatRef = useRef(chat);
  chatRef.current = chat;
  // After any deck change (Add slide, Undo) every card reads its state again: an
  // added slide's card says so, an undone one says that, a stale one offers Prepare
  // again.
  const deckChange = useMemo(() => async (fn) => {
    try {
      return applyRef.current ? await applyRef.current(fn) : null;
    } finally {
      setChangeTick((n) => n + 1);
      transport.load().then((r) => Object.entries(r.proposals || {}).forEach(
        ([id, view]) => chatRef.current.setProposalState(id, view))).catch(() => {});
    }
  }, [transport]);
  const room = useDockRoom(layout);
  // Hidden in Present; the thread (and an answer still coming) stays in `chat`.
  if (chat.available !== true || hidden) return null;
  const drawer = layout === 'drawer' || (room !== null && room < CHAT_WIDTH.min);
  // Remounted when the layout switches, so each keeps its own saved open state.
  return <DockedDeckChat key={drawer ? 'drawer' : 'dock'} chat={chat}
    drawer={drawer} room={room} draft={draft} onDraft={onDraft}
    api={transport} applyChange={deckChange} getDeckContext={getDeckContext}
    changeTick={changeTick} shown={shown} onShown={onShown} />;
}
